// src/components/fix-actions/action-item-compact-row.tsx
import { type FixAction } from "../../services/fix-actions/fix-actions-service";
import { Sparkles, CheckCircle2, Radar, Brain } from "lucide-react";

const getThemeColorVar = (theme?: string): string => {
  if (!theme) return "var(--theme-default)";
  const normalizedTheme = theme.toLowerCase().replace(/\s+/g, "-");
  const themeMap: Record<string, string> = {
    "category-leadership": "var(--theme-category-leadership)",
    "trust-&-transparency": "var(--theme-trust-transparency)",
    "information-freshness": "var(--theme-information-freshness)",
    "knowledge-foundation": "var(--theme-knowledge-foundation)",
  };
  return themeMap[normalizedTheme] || "var(--theme-default)";
};

type SourceBadgeInfo = {
  label: string;
  color: string;
  bg: string;
  border: string;
  Icon: typeof Radar;
} | null;

function getSourceBadge(sourceModule?: string): SourceBadgeInfo {
  const src = String(sourceModule || "").toLowerCase();
  if (src === "antenna") {
    return {
      label: "Brand Monitoring",
      color: "var(--alpha-teal)",
      bg: "var(--alpha-teal-10)",
      border: "var(--alpha-teal-20)",
      Icon: Radar,
    };
  }
  if (src === "cognition") {
    return {
      label: "AI Reasoning Path",
      color: "var(--spectral-violet)",
      bg: "var(--spectral-violet-10)",
      border: "var(--spectral-violet-20)",
      Icon: Brain,
    };
  }
  return null;
}

interface ActionItemCompactRowProps {
  action: FixAction;
  isSelected: boolean;
  isNew?: boolean;
  generatedCount?: number;
  anyGeneratedCount?: number;
  totalArtifacts?: number;
  /** When true, shows a chevron to indicate the row can expand (accordion mode) */
  showExpandIndicator?: boolean;
  /** When true, row is currently expanded (only meaningful when showExpandIndicator) */
  isExpanded?: boolean;
  onClick: () => void;
}

export function ActionItemCompactRow({
  action,
  isSelected,
  isNew = false,
  generatedCount = 0,
  anyGeneratedCount,
  totalArtifacts,
  showExpandIndicator = false,
  isExpanded = false,
  onClick,
}: ActionItemCompactRowProps) {
  const total = totalArtifacts ?? action.artifacts?.length ?? 0;
  const fullyGenerated = total > 0 && generatedCount >= total;
  const progressCount = anyGeneratedCount ?? generatedCount;

  const artifactCount = action.artifacts?.length || 0;
  const themeColorVar = getThemeColorVar(action.strategicTheme);
  const isQuickWin =
    action.effort === "Low" &&
    (String(action.impactLevel).toLowerCase() === "high" ||
      String(action.impactLevel).toLowerCase() === "medium");

  const priorityColor =
    action.priority === "HIGH"
      ? "var(--infrared-coral)"
      : action.priority === "MEDIUM"
      ? "var(--signal-blue)"
      : "var(--muted-foreground)";

  const sourceBadge = getSourceBadge(action.sourceModule);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-lg transition-all"
      style={{
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingLeft: "20px",
        paddingRight: "12px",
        backgroundColor: isSelected ? "var(--background)" : "transparent",
        border: isSelected ? "1px solid var(--border)" : "1px solid transparent",
        cursor: "pointer",
        boxShadow: isSelected ? "var(--elevation-sm)" : "none",
        opacity: fullyGenerated ? 0.78 : 1,
      }}
    >
      <div className="flex items-start" style={{ gap: "var(--gap-sm)" }}>
        <div style={{ position: "relative" }}>
          {isNew && !fullyGenerated && (
            <div
              className="rounded-full"
              style={{
                position: "absolute",
                left: "-12px",
                top: "4px",
                width: "var(--action-dot-size)",
                height: "var(--action-dot-size)",
                backgroundColor: "var(--alpha-teal)",
              }}
            />
          )}

          {fullyGenerated && (
            <div style={{ position: "absolute", left: "-14px", top: "2px" }}>
              <CheckCircle2 className="w-3 h-3" style={{ color: "var(--alpha-teal)" }} />
            </div>
          )}

          <div
            className="rounded-full shrink-0"
            style={{
              width: "var(--action-accent-bar-width)",
              height: "36px",
              backgroundColor: action.priority === "HIGH" ? priorityColor : themeColorVar,
              marginTop: "4px",
            }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div
            className="flex items-center flex-wrap"
            style={{ gap: "6px", marginBottom: "4px" }}
          >
            {isQuickWin && !fullyGenerated && (
              <Sparkles
                className="shrink-0"
                style={{ width: "14px", height: "14px", color: "var(--alpha-teal)" }}
              />
            )}

            {sourceBadge && (
              <span
                className="inline-flex items-center gap-1 rounded"
                style={{
                  padding: "2px 6px",
                  backgroundColor: sourceBadge.bg,
                  color: sourceBadge.color,
                  border: `1px solid ${sourceBadge.border}`,
                  fontSize: "10px",
                  fontWeight: "var(--font-weight-semibold)",
                  whiteSpace: "nowrap",
                  letterSpacing: "0.02em",
                }}
                title={`Source: ${sourceBadge.label}`}
              >
                <sourceBadge.Icon style={{ width: "10px", height: "10px" }} />
                {sourceBadge.label}
              </span>
            )}

            {action.strategicTheme && (
              <span
                className="rounded"
                style={{
                  padding: "2px 6px",
                  fontSize: "10px",
                  fontWeight: "var(--font-weight-semibold)",
                  backgroundColor: "var(--muted)",
                  color: "var(--muted-foreground)",
                  whiteSpace: "nowrap",
                }}
              >
                {action.strategicTheme}
              </span>
            )}

            <span
              style={{
                fontSize: "10px",
                color: "var(--muted-foreground)",
                whiteSpace: "nowrap",
              }}
            >
              {artifactCount} {artifactCount === 1 ? "asset" : "assets"}
            </span>

            {!fullyGenerated && progressCount > 0 && total > 0 && (
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--signal-blue)",
                  fontWeight: "var(--font-weight-semibold)",
                  whiteSpace: "nowrap",
                }}
              >
                · {progressCount}/{total}
              </span>
            )}

            {fullyGenerated && (
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--alpha-teal)",
                  fontWeight: "var(--font-weight-semibold)",
                  whiteSpace: "nowrap",
                }}
              >
                · All generated
              </span>
            )}

            {showExpandIndicator && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "10px",
                  color: "var(--muted-foreground)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {isExpanded ? "▾" : "▸"}
              </span>
            )}
          </div>

          <h5
            style={{
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-weight-semibold)",
              marginBottom: "3px",
              lineHeight: "1.3",
              opacity: action.status === "completed" ? 0.6 : 1,
              textDecoration:
                fullyGenerated || action.status === "completed" ? "line-through" : "none",
              color: fullyGenerated ? "var(--muted-foreground)" : "var(--foreground)",
            }}
          >
            {action.title}
          </h5>

          <p
            style={{
              fontSize: "11px",
              color: "var(--muted-foreground)",
              lineHeight: "1.4",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
            }}
          >
            {action.description}
          </p>
        </div>
      </div>
    </button>
  );
}